import { GAME_FRAME } from "../../config/gameplay";
import { formatCompact } from "../../utils/math";
import { registerLanding } from "../collectors/lanes";
import { lookupProfileProgress } from "../progression/levels";
import { applyCoreReward } from "../rewards/apply";
import type { OrbitForgeSave } from "../save/types";
import type { UpgradeEffects } from "../upgrades/effects";
import type { CollectedCoreHit, CoreState } from "./types";
import type { FeedbackDraft } from "./feedback";
import type { CollectorEventState } from "../collectors/lanes";

export interface CollectionInput {
  readonly save: OrbitForgeSave;
  readonly cores: readonly CoreState[];
  readonly collectedCores: readonly CollectedCoreHit[];
  readonly effects: UpgradeEffects;
  readonly lastPayout: number;
  readonly collectorEvents: CollectorEventState;
}

export interface CollectionResult {
  readonly cores: readonly CoreState[];
  readonly lastPayout: number;
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function applyCollectedCoreRewards(input: CollectionInput): CollectionResult {
  if (input.collectedCores.length === 0) {
    return {
      cores: input.cores,
      lastPayout: input.lastPayout,
      dirty: false,
      feedback: []
    };
  }

  const collected = new Map(input.collectedCores.map((hit) => [hit.coreId, hit]));
  const progress = lookupProfileProgress(input.save.stats);
  const feedback: FeedbackDraft[] = [];
  let lastPayout = input.lastPayout;

  for (const core of input.cores) {
    const hit = collected.get(core.id);
    if (!hit) {
      continue;
    }

    const landing = registerLanding(hit.landingX, progress, input.effects, core.color, input.collectorEvents);
    const reward = applyCoreReward(input.save, core, landing, input.effects);
    input.save.rngState = reward.nextRngState;
    lastPayout = reward.payout;
    feedback.push({
      kind: "lane-hit",
      target: landing.laneIndex >= 0 ? `lane-${landing.laneIndex}` : "collector",
      frameX: hit.landingX + GAME_FRAME.width / 2,
      frameY: 930,
      color: reward.critical ? "gold" : core.color,
      text: `x${Math.max(1, reward.multiplier).toFixed(1)} +${formatCompact(reward.payout)}`
    });
  }

  return {
    cores: input.cores.filter((core) => !collected.has(core.id)),
    lastPayout,
    dirty: true,
    feedback
  };
}
